import { getDeliveryBand, type DeliveryThresholds } from '../../components/dashboard/SiteKPIComponents';
import { SupplierStatus } from './types';
import { STATUS_CONFIG } from './constants';

// Valid onboarding status values from backend
const VALID_STATUSES: SupplierStatus[] = ['draft', 'pending_initial', 'invited', 'not_approved', 'due_diligence', 'approved'];

/**
 * Map the real onboarding_status string from backend to SupplierStatus.
 *
 * `is_approved` is the single source of truth for approval, in both
 * directions. It wins over an unfinished onboarding_status: some prod data
 * (including the DE Avanti-sourced profiles) has onboarding_status='draft'
 * alongside is_approved=true as a migration artefact, and those suppliers
 * should still be treated as approved.
 *
 * CR-6993: the converse case was previously mapped the other way — a profile
 * with onboarding_status='approved' but is_approved=false was reported as
 * approved here while the Onboarding page's Approved tab (which filters on
 * is_approved) excluded it, so the two screens disagreed on the approved
 * count. Such a profile has not passed the approval gate, so it maps to
 * 'pending_initial' ("Pending Approval") instead.
 *
 * @param onboardingStatus - Raw onboarding_status string from the API
 * @param isApproved - The profile's is_approved flag
 * @returns The status the supplier card and approval-gate filter should use
 */
export const mapStatusToType = (
  onboardingStatus: string | undefined,
  isApproved?: boolean,
): SupplierStatus => {
  if (isApproved) return 'approved';
  if (isApproved === false && onboardingStatus === 'approved') return 'pending_initial';
  if (onboardingStatus && VALID_STATUSES.includes(onboardingStatus as SupplierStatus)) {
    return onboardingStatus as SupplierStatus;
  }
  return 'draft';
};

export const getStatusButtonClass = (status: SupplierStatus, isSelected: boolean): string => {
  const config = STATUS_CONFIG[status];
  if (isSelected) {
    return `${config.bgColor} border-2 border-primary`;
  }
  return config.bgColor;
};

/**
 * CR-4497: Get styling for supplier risk level badges.
 * Returns color classes and label for the given risk level.
 *
 * @param riskLevel - The risk level string
 * @returns Style config with badgeColor, label, and icon identifier
 */
export const getRiskLevelStyle = (riskLevel: string): { badgeColor: string; label: string; icon: string } => {
  switch (riskLevel) {
    case 'critical':
      return { badgeColor: 'bg-red-600 text-white', label: 'Critical Risk', icon: 'critical' };
    case 'high':
      return { badgeColor: 'bg-red-100 text-red-800 border-red-200', label: 'High Risk', icon: 'high' };
    case 'medium':
      return { badgeColor: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Medium Risk', icon: 'medium' };
    case 'low':
      return { badgeColor: 'bg-green-100 text-green-800 border-green-200', label: 'Low Risk', icon: 'low' };
    default:
      return { badgeColor: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Unknown Risk', icon: 'medium' };
  }
};

/**
 * CR-8208: the OTIF quick filter grade of a supplier card — its OTIF over
 * the card's Last 12 months on the configured OTIF bands (highest band whose
 * minimum the rate reaches). No judged delivery (N/A) has no grade, so the
 * supplier is counted only under "All", never in the lowest band.
 */
export const cardOtifGrade = (rate: number | null | undefined, bands: DeliveryThresholds): string => {
  if (rate === null || rate === undefined) return '';
  return getDeliveryBand(rate, bands)?.grade ?? '';
};
